import type { WorkspaceDefaultProjectType } from "schemas/generated/workspaceDefaultProjectType";

import { workspaceDefaultProjectTypeSchema } from "schemas/workspaceDefaultProjectType";

// (Yuxin, 2026-07-13, GROW-5633): The signup screen offers two funnels selected
// via `?type=agent` / `?type=shopify`; we persist the chosen funnel so a
// returning visitor without the param lands back on it.
export const signupDefaultProjectTypeStorageKey =
  "replo.signup.defaultProjectType";

const signupProjectTypeByParam: Record<string, WorkspaceDefaultProjectType> = {
  agent: "agent",
  shopify: "shopify_theme_builder",
};

export function getSignupProjectTypeFromParam(
  typeParam: string | null,
): WorkspaceDefaultProjectType | null {
  return typeParam ? signupProjectTypeByParam[typeParam] ?? null : null;
}

// Shopify App Store installs must stay on the Shopify funnel: the backend
// classifies these signups as source=shopify and would discard an "agent"
// funnel choice anyway, so offering the switch would be a bait-and-switch.
const shopifyInstallIntentParams = [
  "nonce",
  "shopify_shop_url",
  "shopifyIntegrationId",
  "pendingAppInstallationId",
];

export function hasShopifyInstallIntent(
  searchParams: URLSearchParams,
): boolean {
  return shopifyInstallIntentParams.some((param) => {
    return Boolean(searchParams.get(param));
  });
}

// (Yuxin, 2026-07-18): Beta opt-in uses the standard auth screens; a `next` back to /beta/enable triggers Agent copy + funnel lock.
export function isAgentEnableAuthIntent({
  pathname,
  searchParams,
}: {
  pathname: string;
  searchParams: URLSearchParams;
}): boolean {
  return (
    pathname.startsWith("/beta/enable") ||
    Boolean(searchParams.get("next")?.startsWith("/beta/enable"))
  );
}

// Campaign links land on signup with their ?source=<code> tucked inside the
// `next` back to /beta/enable, so read it from there before the top level.
export function getCampaignSourceFromParams(
  searchParams: URLSearchParams,
): string | null {
  const nextQueryString = searchParams.get("next")?.split("?")[1];
  const sourceFromNext = nextQueryString
    ? new URLSearchParams(nextQueryString).get("source")
    : null;
  return sourceFromNext ?? searchParams.get("source");
}

export function persistSignupProjectType(
  projectType: WorkspaceDefaultProjectType,
) {
  localStorage.setItem(signupDefaultProjectTypeStorageKey, projectType);
}

export function getPersistedSignupProjectType(): WorkspaceDefaultProjectType | null {
  const parsed = workspaceDefaultProjectTypeSchema.safeParse(
    localStorage.getItem(signupDefaultProjectTypeStorageKey),
  );
  return parsed.success ? parsed.data : null;
}

export function resolveSignupDefaultProjectType({
  userSelected,
  fromParam,
  persisted,
}: {
  userSelected: WorkspaceDefaultProjectType | null;
  fromParam: WorkspaceDefaultProjectType | null;
  persisted: WorkspaceDefaultProjectType | null;
}): WorkspaceDefaultProjectType {
  return userSelected ?? fromParam ?? persisted ?? "shopify_theme_builder";
}
